/**
 * オフライン教材検索
 *
 * - 全 28 章を起動時にインデックス化 (テキスト化 + 小文字)
 * - スコアリング: タイトル一致 (10) > 見出し (5) > 本文 (1)
 * - 全トークン一致を優遇
 * - 結果からスニペット抽出してハイライト
 */

import { ARTICLES } from './data/learn-content.js';

// HTML タグを除去してテキスト化
function stripHtml(html) {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// インデックス: 各セクションを 1 エントリとする
const SEARCH_INDEX = (() => {
  const idx = [];
  for (const a of ARTICLES) {
    // タイトル + 要約のエントリ
    idx.push({
      articleId: a.id,
      chapter: a.chapter,
      title: a.title,
      summary: a.summary,
      sectionIdx: -1,
      sectionHeading: '(章の概要)',
      text: a.title + ' ' + a.summary,
      lower: (a.title + ' ' + a.summary).toLowerCase(),
    });
    // 各セクション
    a.sections.forEach((s, i) => {
      const text = stripHtml(s.body);
      idx.push({
        articleId: a.id,
        chapter: a.chapter,
        title: a.title,
        summary: a.summary,
        sectionIdx: i,
        sectionHeading: s.heading,
        text,
        lower: (s.heading + ' ' + text).toLowerCase(),
      });
    });
  }
  return idx;
})();

/**
 * 全文検索
 * @param {string} query
 * @param {number} limit
 * @returns {Array<{ articleId, chapter, title, sectionHeading, snippet, score }>}
 */
export function search(query, limit = 30) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return [];

  const results = [];
  for (const entry of SEARCH_INDEX) {
    let score = 0;
    let matchedTokens = 0;
    let titleHit = false;
    let headingHit = false;

    const titleLower = entry.title.toLowerCase();
    const headingLower = entry.sectionHeading.toLowerCase();
    const textLower = entry.lower;

    for (const t of tokens) {
      let tokenMatched = false;
      if (titleLower.includes(t)) {
        score += 10;
        titleHit = true;
        tokenMatched = true;
      }
      if (headingLower.includes(t)) {
        score += 5;
        headingHit = true;
        tokenMatched = true;
      }
      if (textLower.includes(t)) {
        score += 1;
        tokenMatched = true;
      }
      if (tokenMatched) matchedTokens++;
    }

    if (matchedTokens === 0) continue;
    // 全トークンマッチを優遇 (倍率)
    if (matchedTokens === tokens.length) score *= 2;
    else if (matchedTokens / tokens.length < 0.5) score *= 0.4;

    results.push({
      articleId: entry.articleId,
      chapter: entry.chapter,
      title: entry.title,
      sectionHeading: entry.sectionHeading,
      sectionIdx: entry.sectionIdx,
      snippet: extractSnippet(entry.text, tokens[0]),
      score,
    });
  }

  // articleId+sectionIdx で重複なし、スコア順
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}

/**
 * クエリ周辺のスニペット (前後 60 字) を抽出
 */
function extractSnippet(text, queryToken, maxLen = 160) {
  const lower = text.toLowerCase();
  const pos = lower.indexOf(queryToken);
  if (pos === -1) {
    return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
  }
  const start = Math.max(0, pos - 50);
  const end = Math.min(text.length, pos + queryToken.length + 90);
  let snippet = text.slice(start, end);
  if (start > 0) snippet = '…' + snippet;
  if (end < text.length) snippet = snippet + '…';
  return snippet;
}

/**
 * クエリトークンを含む文字列に <mark> ハイライトを入れる (XSS 安全のため
 * テキストノード分割で処理)
 */
export function highlightHtml(text, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q || !text) return escapeHtml(text);
  const tokens = [...new Set(q.split(/\s+/).filter(Boolean))];
  if (tokens.length === 0) return escapeHtml(text);

  // 一度エスケープしてから、トークンの位置をハイライト
  const escaped = escapeHtml(text);
  const lower = escaped.toLowerCase();

  const ranges = [];
  for (const t of tokens) {
    let i = 0;
    const tLow = escapeHtml(t).toLowerCase();
    while (true) {
      const p = lower.indexOf(tLow, i);
      if (p === -1) break;
      ranges.push([p, p + tLow.length]);
      i = p + tLow.length;
    }
  }
  if (ranges.length === 0) return escaped;
  ranges.sort((a, b) => a[0] - b[0]);
  // マージ
  const merged = [];
  for (const r of ranges) {
    if (merged.length === 0 || merged[merged.length - 1][1] < r[0]) {
      merged.push([...r]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], r[1]);
    }
  }
  let out = '';
  let last = 0;
  for (const [s, e] of merged) {
    out += escaped.slice(last, s) + '<mark>' + escaped.slice(s, e) + '</mark>';
    last = e;
  }
  out += escaped.slice(last);
  return out;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 教材全文をマークダウン風テキストに変換 (AI prompt 用)
 * 章ごとに ID とタイトル・本文をテキスト化
 */
export function getLearnContentAsMarkdown() {
  return ARTICLES.map((a) => {
    const sections = a.sections.map((s) => `### ${s.heading}\n${stripHtml(s.body)}`).join('\n\n');
    return `## ${a.chapter} [id:${a.id}] ${a.title}\n${a.summary}\n\n${sections}`;
  }).join('\n\n---\n\n');
}
